import type { Command, CommandOutput } from '../../types/commands'
import { colorize, formatHighlight } from '../colorScheme'

/**
 * Configuration for a single setting option
 */
interface SettingConfig {
  aliases: string[]        // Alternative names for this setting
  validValues: string[]    // Allowed values
  defaultValue: string     // Default value
  storageKey: string       // localStorage key
  attribute: string        // document.documentElement attribute name
  displayName: string      // Human-readable name for display
}

/**
 * All available settings configurations
 */
const SETTINGS_CONFIG: Record<string, SettingConfig> = {
  font: {
    aliases: ['font', 'fontsize'],
    validValues: ['small', 'medium', 'large', 'xlarge'],
    defaultValue: 'medium',
    storageKey: 'terminal-font-size',
    attribute: 'data-font-size',
    displayName: 'Font Size',
  },
  contrast: {
    aliases: ['contrast'],
    validValues: ['normal', 'high', 'low'],
    defaultValue: 'normal',
    storageKey: 'terminal-contrast',
    attribute: 'data-contrast',
    displayName: 'Contrast',
  },
}

/**
 * Find setting config by option name (including aliases)
 */
function findSettingConfig(option: string): SettingConfig | null {
  const lowerOption = option.toLowerCase()
  return (
    Object.values(SETTINGS_CONFIG).find((config) =>
      config.aliases.includes(lowerOption)
    ) || null
  )
}

/**
 * Apply a setting value
 */
function applySetting(config: SettingConfig, value: string): CommandOutput[] {
  if (!config.validValues.includes(value)) {
    return [
      {
        type: 'error',
        content: `Invalid ${config.displayName.toLowerCase()}: ${value}\n\nValid options: ${config.validValues.join(' | ')}`,
      },
    ]
  }

  localStorage.setItem(config.storageKey, value)
  document.documentElement.setAttribute(config.attribute, value)

  return [
    {
      type: 'success',
      content: `✓ ${config.displayName} changed to: ${value}\n\nSettings saved and will persist across sessions.`,
    },
  ]
}

export const settingsCommand: Command = {
  name: 'settings',
  description: 'Adjust accessibility and display settings',
  usage: 'settings [option] [value]',
  aliases: ['config', 'preferences'],
  category: 'system',
  execute: (args) => {
    // If no arguments, show current settings
    if (args.length === 0) {
      const fontConfig = SETTINGS_CONFIG.font
      const contrastConfig = SETTINGS_CONFIG.contrast

      const currentFontSize = localStorage.getItem(fontConfig.storageKey) || fontConfig.defaultValue
      const currentContrast = localStorage.getItem(contrastConfig.storageKey) || contrastConfig.defaultValue

      const settingsText = `
╭─ ⚙️  ${formatHighlight('ACCESSIBILITY SETTINGS')} ───────────────────╮
│                                                     │
│  ${formatHighlight(fontConfig.displayName)}: ${colorize(currentFontSize, 'cyan')}                              │
│    Options: ${fontConfig.validValues.join(' | ')}       │
│    Usage: ${colorize(`settings ${fontConfig.aliases[0]} <size>`, 'yellow')}                   │
│                                                     │
│  ${formatHighlight(contrastConfig.displayName)}: ${colorize(currentContrast, 'cyan')}                               │
│    Options: ${contrastConfig.validValues.join(' | ')}                    │
│    Usage: ${colorize(`settings ${contrastConfig.aliases[0]} <mode>`, 'yellow')}               │
│                                                     │
│  ${colorize('💡 Tip:', 'brightYellow')} Settings are saved automatically        │
│      and persist across sessions                   │
│                                                     │
╰─────────────────────────────────────────────────────╯
`

      return [
        {
          type: 'info',
          content: (
            <div dangerouslySetInnerHTML={{ __html: settingsText }} />
          ),
        },
      ]
    }

    const option = args[0]?.toLowerCase()
    const value = args[1]?.toLowerCase()

    // Handle reset setting
    if (option === 'reset') {
      // Reset all settings to defaults
      Object.values(SETTINGS_CONFIG).forEach((config) => {
        localStorage.setItem(config.storageKey, config.defaultValue)
        document.documentElement.setAttribute(config.attribute, config.defaultValue)
      })

      const resetMessages = Object.values(SETTINGS_CONFIG).map(
        (config) => `${config.displayName}: ${config.defaultValue}`
      )

      return [
        {
          type: 'success',
          content: `✓ Settings reset to defaults\n\n${resetMessages.join('\n')}`,
        },
      ]
    }

    // Find the setting configuration
    const config = findSettingConfig(option)

    if (!config) {
      const availableSettings = Object.keys(SETTINGS_CONFIG)
        .map((key) => `  ${colorize(key, 'cyan')} - Adjust ${SETTINGS_CONFIG[key].displayName.toLowerCase()}`)
        .join('\n')

      const errorText = `Unknown setting: ${option}\n\nAvailable settings:\n${availableSettings}\n  ${colorize('reset', 'cyan')} - Reset to defaults\n\nUse ${colorize('settings', 'yellow')} with no arguments to see current values.`

      return [
        {
          type: 'error',
          content: (
            <div dangerouslySetInnerHTML={{ __html: errorText }} />
          ),
        },
      ]
    }

    // Check if value was provided
    if (!value) {
      return [
        {
          type: 'error',
          content: `Please specify a ${config.displayName.toLowerCase()}.\n\nUsage: settings ${config.aliases[0]} <value>\nOptions: ${config.validValues.join(' | ')}`,
        },
      ]
    }

    // Apply the setting
    return applySetting(config, value)
  },
}
